import type { Ship, NavalVessel } from '@/types';

// ---------------------------------------------------------------------------
// Ship Tracker — Simulated AIS data on major shipping lanes
// No free real-time AIS API exists; positions advance each call
// ---------------------------------------------------------------------------

interface LanePoint { lat: number; lon: number; }

const SHIPPING_LANES: { name: string; waypoints: LanePoint[]; traffic: number }[] = [
  // ── Core arteries ────────────────────────────────────────────────────────
  {
    name: 'Trans-Pacific (West)',
    traffic: 14,
    waypoints: [
      { lat: 35.6, lon: 139.8 }, { lat: 30.0, lon: 155.0 }, { lat: 20.0, lon: 175.0 },
      { lat: 10.0, lon: -160.0 }, { lat: 0, lon: -140.0 }, { lat: -5, lon: -120.0 },
      { lat: 3.0, lon: -77.0 },
    ],
  },
  {
    name: 'Trans-Pacific (North)',
    traffic: 10,
    waypoints: [
      { lat: 35.0, lon: 135.0 }, { lat: 42.0, lon: 150.0 }, { lat: 50.0, lon: 168.0 },
      { lat: 53.0, lon: -170.0 }, { lat: 52.0, lon: -155.0 }, { lat: 47.5, lon: -125.0 },
    ],
  },
  {
    name: 'Strait of Malacca',
    traffic: 16,
    waypoints: [
      { lat: 1.2, lon: 104.0 }, { lat: 3.5, lon: 101.0 }, { lat: 5.5, lon: 99.5 },
      { lat: 8.0, lon: 99.0 }, { lat: 10.0, lon: 98.5 },
    ],
  },
  {
    name: 'Suez Canal / Mediterranean',
    traffic: 12,
    waypoints: [
      { lat: 30.0, lon: 32.5 }, { lat: 31.5, lon: 32.3 }, { lat: 32.5, lon: 32.0 },
      { lat: 34.0, lon: 33.5 }, { lat: 35.5, lon: 24.0 }, { lat: 37.0, lon: 15.0 },
      { lat: 38.0, lon: 5.0 }, { lat: 36.0, lon: -5.5 },
    ],
  },
  {
    name: 'Strait of Hormuz / Persian Gulf',
    traffic: 10,
    waypoints: [
      { lat: 24.0, lon: 56.5 }, { lat: 26.0, lon: 56.0 }, { lat: 27.0, lon: 55.0 },
      { lat: 26.5, lon: 53.0 }, { lat: 26.0, lon: 50.0 }, { lat: 25.5, lon: 57.5 },
      { lat: 22.0, lon: 60.0 },
    ],
  },
  {
    name: 'South China Sea',
    traffic: 15,
    waypoints: [
      { lat: 1.3, lon: 104.0 }, { lat: 5.0, lon: 109.0 }, { lat: 10.0, lon: 114.0 },
      { lat: 15.0, lon: 117.0 }, { lat: 20.0, lon: 118.0 }, { lat: 22.3, lon: 114.2 },
    ],
  },
  {
    name: 'English Channel / North Sea',
    traffic: 12,
    waypoints: [
      { lat: 48.5, lon: -5.0 }, { lat: 49.5, lon: -3.0 }, { lat: 50.5, lon: -1.0 },
      { lat: 51.0, lon: 1.5 }, { lat: 51.5, lon: 3.0 }, { lat: 52.5, lon: 5.0 },
      { lat: 54.0, lon: 8.0 }, { lat: 57.0, lon: 10.0 },
    ],
  },
  {
    name: 'Cape of Good Hope',
    traffic: 8,
    waypoints: [
      { lat: -15.0, lon: 12.0 }, { lat: -25.0, lon: 15.0 }, { lat: -34.5, lon: 18.5 },
      { lat: -40.0, lon: 20.0 }, { lat: -38.0, lon: 40.0 }, { lat: -25.0, lon: 50.0 },
    ],
  },
  {
    name: 'US East Coast / Caribbean',
    traffic: 10,
    waypoints: [
      { lat: 40.7, lon: -74.0 }, { lat: 35.0, lon: -75.0 }, { lat: 25.0, lon: -80.0 },
      { lat: 19.0, lon: -70.0 }, { lat: 10.0, lon: -65.0 }, { lat: 8.5, lon: -79.5 },
    ],
  },
  // ── Additional strategic lanes ────────────────────────────────────────────
  {
    name: 'North Atlantic (Europe-USA)',
    traffic: 12,
    waypoints: [
      { lat: 50.0, lon: -5.0 }, { lat: 50.5, lon: -20.0 }, { lat: 48.0, lon: -35.0 },
      { lat: 44.0, lon: -50.0 }, { lat: 42.0, lon: -65.0 }, { lat: 40.5, lon: -74.0 },
    ],
  },
  {
    name: 'Indian Ocean (Asia-Europe)',
    traffic: 11,
    waypoints: [
      { lat: 6.0,  lon: 80.0 }, { lat: 4.0, lon: 73.0 }, { lat: 5.0, lon: 60.0 },
      { lat: 10.0, lon: 50.0 }, { lat: 12.5, lon: 43.5 }, { lat: 15.0, lon: 42.0 },
    ],
  },
  {
    name: 'Red Sea (Bab-el-Mandeb)',
    traffic: 8,
    waypoints: [
      { lat: 12.5, lon: 43.5 }, { lat: 15.0, lon: 42.0 }, { lat: 18.0, lon: 41.0 },
      { lat: 20.0, lon: 38.5 }, { lat: 22.0, lon: 37.0 }, { lat: 27.5, lon: 34.0 },
    ],
  },
  {
    name: 'Baltic Sea',
    traffic: 9,
    waypoints: [
      { lat: 57.0, lon: 10.0 }, { lat: 57.5, lon: 12.0 }, { lat: 56.0, lon: 15.0 },
      { lat: 57.0, lon: 18.0 }, { lat: 58.0, lon: 21.5 }, { lat: 60.0, lon: 25.0 },
    ],
  },
  {
    name: 'East Asia Coastal',
    traffic: 12,
    waypoints: [
      { lat: 22.3, lon: 114.2 }, { lat: 25.0, lon: 121.5 }, { lat: 29.0, lon: 122.0 },
      { lat: 32.0, lon: 122.5 }, { lat: 35.5, lon: 130.0 }, { lat: 35.0, lon: 136.0 },
    ],
  },
  {
    name: 'Australia / Oceania',
    traffic: 7,
    waypoints: [
      { lat: -17.0, lon: 146.0 }, { lat: -23.0, lon: 152.0 }, { lat: -34.0, lon: 151.0 },
      { lat: -42.0, lon: 147.0 }, { lat: -41.0, lon: 174.5 },
    ],
  },
  {
    name: 'West Africa Coast',
    traffic: 6,
    waypoints: [
      { lat: 14.5, lon: -17.0 }, { lat: 10.0, lon: -15.0 }, { lat: 5.5, lon: -0.5 },
      { lat: 1.0,  lon: 3.5 }, { lat: -5.0, lon: 12.0 }, { lat: -8.8, lon: 13.2 },
    ],
  },
  {
    name: 'US West Coast / Pacific',
    traffic: 8,
    waypoints: [
      { lat: 47.5, lon: -122.5 }, { lat: 37.5, lon: -122.5 }, { lat: 34.0, lon: -118.0 },
      { lat: 24.0, lon: -110.0 }, { lat: 9.0,  lon: -79.5 },
    ],
  },
  {
    name: 'South America East Coast',
    traffic: 7,
    waypoints: [
      { lat: 10.0, lon: -63.0 }, { lat: 0.0,  lon: -50.0 }, { lat: -8.0, lon: -35.0 },
      { lat: -23.0, lon: -43.0 }, { lat: -34.5, lon: -58.5 }, { lat: -38.0, lon: -57.0 },
    ],
  },
];

const SHIP_NAMES = [
  // Container mega-ships
  'Ever Given', 'MSC Oscar', 'HMM Algeciras', 'CSCL Globe', 'MOL Triumph',
  'Maersk Madrid', 'CMA CGM Antoine', 'Evergreen Marine', 'OOCL Hong Kong',
  'Yang Ming Witness', 'Cosco Shipping Universe', 'MSC Irina', 'HMM Nuri',
  'Ever Ace', 'CMA CGM Skanderborg', 'MSC Loreto', 'ONE Innovation',
  'Maersk Elba', 'Evergreen Elegance', 'Yang Ming Ubiquity',
  // Tankers
  'Tsakos Energy', 'Euronav Sirius', 'Teekay Courage', 'Nordic Oshkosh',
  'VLCC Oceana', 'BW Mindoro', 'DHT Amazon', 'Frontline Lyfja',
  'Stena Sunrise', 'Ardmore Shipping', 'Euronav Luxembourg', 'Tsakos Apollon',
  'Front Altair', 'BW Rhine', 'Nordic Hunter', 'Overseas Petromar',
  // Bulk carriers
  'Pacific Basin', 'Nordic Grace', 'Star Polaris', 'Baltic Ace',
  'Pacific Crown', 'Atlantic Hawk', 'Star Triumph', 'Pacific Light',
  'Capesize Atlantic', 'Star Quantum', 'Nordic Bulk', 'Globus Garnet',
  // Smaller vessels
  'Seaspan Dragon', 'Wan Hai 275', 'NYK Falcon', 'Costamare Christos',
  'Danaos Shipping', 'Pacific Seagull', 'Atlantic Condor', 'Indian Star',
  'Orient Explorer', 'Aegean Sky', 'Black Sea Pioneer', 'Baltic Spring',
  // Passenger / ferry
  'MSC Seashore', 'Royal Caribbean Harmony', 'Costa Smeralda', 'Norwegian Bliss',
  'Anthem of the Seas', 'Queen Mary 2', 'Brittany Ferries', 'Viking Sky',
];

const SHIP_FLAGS = [
  { name: 'Panama', code: 'PA' }, { name: 'Liberia', code: 'LR' },
  { name: 'Marshall Islands', code: 'MH' }, { name: 'Hong Kong', code: 'HK' },
  { name: 'Singapore', code: 'SG' }, { name: 'Bahamas', code: 'BS' },
  { name: 'Malta', code: 'MT' }, { name: 'China', code: 'CN' },
  { name: 'Cyprus', code: 'CY' }, { name: 'Cayman Islands', code: 'KY' },
  { name: 'Greece', code: 'GR' }, { name: 'Norway', code: 'NO' },
  { name: 'Japan', code: 'JP' }, { name: 'South Korea', code: 'KR' },
  { name: 'Germany', code: 'DE' }, { name: 'Netherlands', code: 'NL' },
  { name: 'Bermuda', code: 'BM' }, { name: 'Denmark', code: 'DK' },
  { name: 'United Kingdom', code: 'GB' }, { name: 'United States', code: 'US' },
];

const SHIP_TYPES: Ship['type'][] = ['cargo', 'container', 'tanker', 'bulk', 'cargo', 'container', 'tanker', 'passenger', 'bulk', 'cargo'];

function interpolatePoint(waypoints: LanePoint[], t: number): LanePoint {
  const n = waypoints.length - 1;
  const segIdx = Math.min(Math.floor(t * n), n - 1);
  const segT = (t * n) - segIdx;
  const a = waypoints[segIdx];
  const b = waypoints[segIdx + 1];
  return {
    lat: a.lat + (b.lat - a.lat) * segT,
    lon: a.lon + (b.lon - a.lon) * segT,
  };
}

function headingBetween(a: LanePoint, b: LanePoint): number {
  const dLon = b.lon - a.lon;
  const dLat = b.lat - a.lat;
  const angle = Math.atan2(dLon, dLat) * (180 / Math.PI);
  return (angle + 360) % 360;
}

let shipCache: Ship[] | null = null;
let shipCacheTime = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 min

export function fetchShipData(): Ship[] {
  const now = Date.now();
  if (shipCache && now - shipCacheTime < CACHE_TTL) return shipCache;

  const ships: Ship[] = [];
  let shipIndex = 0;

  for (const lane of SHIPPING_LANES) {
    for (let i = 0; i < lane.traffic; i++) {
      // Each ship is at a different point along the lane, advancing over time
      const baseT = (i / lane.traffic);
      const timeOffset = ((now / (1000 * 3600 * 24)) % 1); // 0-1 daily cycle
      const t = (baseT + timeOffset * 0.3) % 1;

      const point = interpolatePoint(lane.waypoints, t);
      const nextT = Math.min(t + 0.02, 1);
      const nextPoint = interpolatePoint(lane.waypoints, nextT);
      const heading = headingBetween(point, nextPoint);

      const nameIdx = shipIndex % SHIP_NAMES.length;
      const flagIdx = shipIndex % SHIP_FLAGS.length;
      const typeIdx = shipIndex % SHIP_TYPES.length;

      ships.push({
        id: `ship-${lane.name}-${i}`,
        name: SHIP_NAMES[nameIdx],
        type: SHIP_TYPES[typeIdx],
        lat: point.lat + (Math.sin(shipIndex * 7.3) * 0.15),
        lon: point.lon + (Math.cos(shipIndex * 7.3) * 0.15),
        heading,
        speed: 12 + (shipIndex % 8),
        flag: SHIP_FLAGS[flagIdx].name,
        flagCode: SHIP_FLAGS[flagIdx].code,
      });

      shipIndex++;
    }
  }

  shipCache = ships;
  shipCacheTime = now;
  return ships;
}

// ---------------------------------------------------------------------------
// Naval Vessels — based on publicly known deployments (OSINT)
// ---------------------------------------------------------------------------

export const NAVAL_VESSELS: NavalVessel[] = [
  // US Navy
  { id: 'cv-78', name: 'USS Gerald R. Ford', type: 'carrier', lat: 36.5, lon: -8.5, heading: 45, country: 'United States', countryCode: 'US', groupName: 'CSG-12' },
  { id: 'cv-76', name: 'USS Ronald Reagan', type: 'carrier', lat: 14.5, lon: 144.8, heading: 270, country: 'United States', countryCode: 'US', groupName: 'CSG-5' },
  { id: 'cv-77', name: 'USS George H.W. Bush', type: 'carrier', lat: 37.0, lon: -75.0, heading: 180, country: 'United States', countryCode: 'US', groupName: 'CSG-2' },
  { id: 'ddg-102', name: 'USS Sampson', type: 'destroyer', lat: 35.0, lon: 24.0, heading: 90, country: 'United States', countryCode: 'US' },
  { id: 'ddg-91', name: 'USS Pinckney', type: 'destroyer', lat: 25.0, lon: 130.0, heading: 270, country: 'United States', countryCode: 'US' },
  { id: 'ssbn-731', name: 'USS Alabama', type: 'submarine', lat: 48.0, lon: -130.0, heading: 180, country: 'United States', countryCode: 'US' },

  // China PLAN
  { id: 'cv-17', name: 'CNS Shandong', type: 'carrier', lat: 20.0, lon: 118.0, heading: 135, country: 'China', countryCode: 'CN', groupName: 'CSG' },
  { id: 'cv-18', name: 'CNS Fujian', type: 'carrier', lat: 30.0, lon: 122.5, heading: 90, country: 'China', countryCode: 'CN', groupName: 'CSG' },
  { id: 'cn-sub-1', name: 'Type 094 Jin-class', type: 'submarine', lat: 22.0, lon: 109.0, heading: 200, country: 'China', countryCode: 'CN' },
  { id: 'cn-des-1', name: 'CNS Nanchang', type: 'destroyer', lat: 12.0, lon: 112.0, heading: 300, country: 'China', countryCode: 'CN' },

  // Russia
  { id: 'ru-cv-1', name: 'Admiral Kuznetsov', type: 'carrier', lat: 69.5, lon: 33.0, heading: 180, country: 'Russia', countryCode: 'RU', groupName: 'Northern Fleet' },
  { id: 'ru-sub-1', name: 'K-329 Belgorod', type: 'submarine', lat: 70.0, lon: 22.0, heading: 270, country: 'Russia', countryCode: 'RU' },
  { id: 'ru-cru-1', name: 'Marshal Ustinov', type: 'cruiser', lat: 44.5, lon: 35.0, heading: 90, country: 'Russia', countryCode: 'RU', groupName: 'Black Sea Fleet' },
  { id: 'ru-des-1', name: 'Udaloy-class', type: 'destroyer', lat: 55.0, lon: 20.0, heading: 300, country: 'Russia', countryCode: 'RU', groupName: 'Baltic Fleet' },

  // UK Royal Navy
  { id: 'uk-cv-1', name: 'HMS Queen Elizabeth', type: 'carrier', lat: 50.5, lon: -4.0, heading: 200, country: 'United Kingdom', countryCode: 'GB', groupName: 'CSG21' },
  { id: 'uk-sub-1', name: 'HMS Vanguard', type: 'submarine', lat: 57.0, lon: -7.0, heading: 270, country: 'United Kingdom', countryCode: 'GB' },

  // France
  { id: 'fr-cv-1', name: 'FS Charles de Gaulle', type: 'carrier', lat: 43.3, lon: 5.0, heading: 270, country: 'France', countryCode: 'FR', groupName: 'TF473' },

  // India
  { id: 'in-cv-1', name: 'INS Vikrant', type: 'carrier', lat: 15.5, lon: 74.0, heading: 180, country: 'India', countryCode: 'IN' },

  // Japan JMSDF
  { id: 'jp-dd-1', name: 'JS Izumo', type: 'amphibious', lat: 35.4, lon: 135.0, heading: 90, country: 'Japan', countryCode: 'JP' },

  // Iran
  { id: 'ir-nav-1', name: 'IRIS Makran', type: 'amphibious', lat: 25.0, lon: 57.5, heading: 270, country: 'Iran', countryCode: 'IR' },

  // North Korea
  { id: 'kp-sub-1', name: 'Romeo-class', type: 'submarine', lat: 38.5, lon: 127.5, heading: 180, country: 'North Korea', countryCode: 'KP' },
];

let navalCache: NavalVessel[] | null = null;
let navalCacheTime = 0;

export function fetchNavalVessels(): NavalVessel[] {
  const now = Date.now();
  if (navalCache && now - navalCacheTime < CACHE_TTL) return navalCache;

  // Add subtle movement to vessels
  navalCache = NAVAL_VESSELS.map((v, i) => ({
    ...v,
    lat: v.lat + Math.sin(now / 3600000 + i * 2.3) * 0.15,
    lon: v.lon + Math.cos(now / 3600000 + i * 2.3) * 0.15,
  }));
  navalCacheTime = now;
  return navalCache;
}
